(function () {
  'use strict';

  const COOKIE_NAME = 'sm_ref';
  const COOKIE_REFERRAL_ID = 'sm_referral_id';
  const COOKIE_REF_LINK = 'sm_ref_link';
  const COOKIE_UTM_SOURCE = 'sm_utm_source';
  const COOKIE_UTM_MEDIUM = 'sm_utm_medium';
  /** Unix ms of last successful track-click for the current sm_ref; Expires matches backend cookie_date. */
  const COOKIE_REF_TRACKED_AT = 'sm_ref_tracked_at';
  const TRACK_DEDUPE_MS = 24 * 60 * 60 * 1000;

  const API_ENDPOINT = window.shopUrl === 'https://summitmetals.com'
    ? 'https://affiliate-api.summitmetals.com/api/affiliate/track-click'
    : 'https://staging-affiliate-api.summitmetals.com/api/affiliate/track-click';

  function setCookieWithExpiry(name, value, expiryDate) {
    const expires = `expires=${new Date(expiryDate).toUTCString()}`;
    document.cookie = `${name}=${encodeURIComponent(value)};${expires};path=/;SameSite=Lax`;
  }

  function getCookie(name) {
    const cookies = document.cookie.split('; ');
    for (let cookie of cookies) {
      const [key, value] = cookie.split('=');
      if (key === name) {
        return decodeURIComponent(value);
      }
    }
    return null;
  }

  function getURLParameter(param) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  }

  /**
   * Dedupe uses the same cookie system as attribution (backend cookie_date on success):
   * - No sm_ref cookie yet → send track-click (first visit with ?ref=).
   * - URL ref !== stored sm_ref → send track (different affiliate).
   * - URL ref === sm_ref and last track &lt; 24h → skip (dedupe).
   * - Same ref but ≥ 24h since last track → send track again.
   */
  function shouldSkipTrackDueToRefAndTime(affiliateCode) {
    if (!affiliateCode) return false;
    var storedRef = getCookie(COOKIE_NAME);
    if (!storedRef) return false;
    if (affiliateCode !== storedRef) return false;

    var rawTs = getCookie(COOKIE_REF_TRACKED_AT);
    if (!rawTs) return false;

    var ts = parseInt(rawTs, 10);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < TRACK_DEDUPE_MS;
  }

  function getUTMParamsFromUrl(urlString) {
    try {
      const url = new URL(urlString);
      const params = url.searchParams;
      return {
        utm_source: params.get('utm_source'),
        utm_medium: params.get('utm_medium'),
        utm_campaign: params.get('utm_campaign'),
        utm_content: params.get('utm_content'),
        utm_term: params.get('utm_term'),
      };
    } catch (e) {
      return {
        utm_source: null,
        utm_medium: null,
        utm_campaign: null,
        utm_content: null,
        utm_term: null,
      };
    }
  }

  async function trackClick(affiliateCode, refLink) {
    if (!affiliateCode) return;

    if (shouldSkipTrackDueToRefAndTime(affiliateCode)) {
      return;
    }

    try {
      const utm = getUTMParamsFromUrl(refLink || window.location.href);

      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ref_code: affiliateCode,
          referrer_url: refLink || null,
          utm_source: utm.utm_source ?? null,
          utm_medium: utm.utm_medium ?? null,
          utm_campaign: utm.utm_campaign ?? null,
          utm_content: utm.utm_content ?? null,
          utm_term: utm.utm_term ?? null,
        }),
        keepalive: true
      });

      const data = await res.json().catch(function () {
        return null;
      });
      if (!data) {
        console.debug('Affiliate tracking: invalid JSON response');
        return;
      }

      if (!res.ok) {
        console.debug('Affiliate tracking API returned error:', res.status);
        return;
      }

      var referral_id = data.referral_id;
      var cookie_date = data.cookie_date;
      if (!referral_id || !cookie_date) {
        console.debug('Affiliate tracking: missing referral_id or cookie_date in response');
        return;
      }

      var trackedAtMs = data.clicked_at
        ? new Date(data.clicked_at).getTime()
        : Date.now();
      if (Number.isNaN(trackedAtMs)) {
        trackedAtMs = Date.now();
      }

      setCookieWithExpiry(COOKIE_NAME, affiliateCode, cookie_date);
      setCookieWithExpiry(COOKIE_REFERRAL_ID, referral_id, cookie_date);
      setCookieWithExpiry(COOKIE_REF_TRACKED_AT, String(trackedAtMs), cookie_date);
      if (refLink) {
        setCookieWithExpiry(COOKIE_REF_LINK, refLink, cookie_date);
      }
      // Persist UTM for checkout handoff after URL is cleaned (ref + utm stripped from address bar).
      setCookieWithExpiry(COOKIE_UTM_SOURCE, utm.utm_source != null ? utm.utm_source : '', cookie_date);
      setCookieWithExpiry(COOKIE_UTM_MEDIUM, utm.utm_medium != null ? utm.utm_medium : '', cookie_date);

    } catch (error) {
      console.debug('Affiliate tracking API call failed:', error);
    }
  }

  function stripAffiliateQueryParams(urlString) {
    var url = new URL(urlString);
    url.searchParams.delete('ref');
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (key) {
      url.searchParams.delete(key);
    });
    return url;
  }

  function initAffiliateTracking() {
    const refParam = getURLParameter('ref');

    if (refParam) {
      trackClick(refParam, window.location.href);

      const url = stripAffiliateQueryParams(window.location.href);
      window.history.replaceState({}, '', url);
    }
  }

  function getAffiliateData() {
    const refCode = getCookie(COOKIE_NAME);
    const referralId = getCookie(COOKIE_REFERRAL_ID);
    if (!refCode || !referralId) return null;

    const refLink = getCookie(COOKIE_REF_LINK);
    const parsed = refLink ? getUTMParamsFromUrl(refLink) : null;
    var cookieSrc = getCookie(COOKIE_UTM_SOURCE);
    var cookieMed = getCookie(COOKIE_UTM_MEDIUM);
        var utm_source = (cookieSrc !== null && cookieSrc !== '') ? cookieSrc : (parsed ? parsed.utm_source : null);
    var utm_medium = (cookieMed !== null && cookieMed !== '') ? cookieMed : (parsed ? parsed.utm_medium : null);

    return {
      ref_code: refCode,
      referral_id: referralId,
      utm_source: utm_source || null,
      utm_medium: utm_medium || null,
      utm_campaign: parsed ? parsed.utm_campaign : null,
    };
  }

  function getAffiliateCode() {
    return getCookie(COOKIE_NAME);
  }

  function appendRefToCheckoutUrl(checkoutUrl) {
    const affiliateData = getAffiliateData();
    if (!affiliateData) return checkoutUrl;

    const separator = checkoutUrl.includes('?') ? '&' : '?';
    let url = `${checkoutUrl}${separator}ref_code=${encodeURIComponent(affiliateData.ref_code)}&referral_id=${encodeURIComponent(affiliateData.referral_id)}`;
    if (affiliateData.utm_source) {
      url += `&utm_source=${encodeURIComponent(affiliateData.utm_source)}`;
    }
    if (affiliateData.utm_medium) {
      url += `&utm_medium=${encodeURIComponent(affiliateData.utm_medium)}`;
    }
    return url;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAffiliateTracking);
  } else {
    initAffiliateTracking();
  }

  window.AffiliateTracking = {
    getAffiliateCode: getAffiliateCode,
    appendRefToCheckoutUrl: appendRefToCheckoutUrl
  };
})();
